<template lang="pug">
.history-online-meeting-list-tab
  .history-period.d-flex.flex-row.align-items-center.mt-2
    .title.mr-2 履歴範囲
    b-form-select.history-option#history-period(v-model="historyPeriod" :options="historyPeriodOptions" size="md" @change="changeHistoryPeriod")
  .mb-2
  OnlineMeetingTable(v-model="items" :fields="fields" :totalRows.sync="rows" :currentPage.sync="currentPage" :isLoading="isLoading"
    :perPage="perPage" :filter="filter" @onDetail="onDetail")
  .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
    // CSVダウンロード
    .d-flex.flex-fill
      button.btn(@click="onDownload") ダウンロード
    // 検索
    .search-filter.d-flex.mr-1.align-items-center.mr-2
      b-input-group.search-filter-input-group.mr-1(size="md")
        b-input-group-prepend
          b-input-group-text.search-filter-icon
            .search-icon
        b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="history-search-filter-help" title="検索項目について")
        template(v-slot:text)
          div 検索項目は以下になります。
          div ・件名
    // フィルタリング
    span.table-option-text 会議相手:
    .filter
      b-form-select(id="history-filter-input" v-model="filter.id" :options="filterOptions" size="md")
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(id="history-per-page-select" v-model="perPage" :options="pageOptions" size="md" @change="selectPerPage")
    // ページネーション
    b-pagination.m-0(v-model="currentPage" :total-rows="rows" :per-page="perPage" :disabled="isLoading" @change="selectPage" hide-goto-end-buttons)
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";

import { userStore, itemListFilterStore } from "@/store/store";
import { tabIndexesStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";
import Setting from "../../../../public/setting.json";

import OtherOfficeOnlineMeetingsService, { OtherOfficeOnlineMeetingGetModel } from "@/commons/services/OtherOfficeOnlineMeetings";
import OtherOfficeConnectionsService, { OtherOfficeConnectionGetModel } from "@/commons/services/OtherOfficeConnections";

import OnlineMeetingTable, { FilterSetting, OnlineMeetingTableField, OnlineMeetingTableItem } from "@/pc/containers/otherOfficeOnlineMeetings/OnlineMeetingTable.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import { errorHandle } from "@/errorHandler";

/**
 * ### 仮押さえ／転床一覧タブコンポーネント
 */
@Component({
  components: {
    HelpIcon,
    OnlineMeetingTable
  }
})
export default class HistoryOnlineMeetingListTab extends Vue {
  /** 待機者ラベルサービス */
  @Inject("OtherOfficeOnlineMeetingsService") meetingsService!: OtherOfficeOnlineMeetingsService;
  /** 他事業所連携管理サービス */
  @Inject("OtherOfficeConnectionsService") otherOfficeConnectionsService!: OtherOfficeConnectionsService;

  /** タブインデックス */
  @Prop()
  tabIndex!: number;

  /** 履歴範囲 */
  historyPeriod: number = HistoryPeriodOptionValue.THREE_MONTH;
  /** 履歴範囲選択肢 */
  historyPeriodOptions: Array<{ value: number | string; text: string }> = [
    { value: HistoryPeriodOptionValue.THREE_MONTH, text: "3ヵ月" },
    { value: HistoryPeriodOptionValue.SIX_MONTH, text: "6ヵ月" },
    { value: HistoryPeriodOptionValue.ONE_YEAR, text: "1年" },
    { value: HistoryPeriodOptionValue.THREE_YEAR, text: "3年" },
    { value: HistoryPeriodOptionValue.ALL, text: "全て" }
  ];

  /** テーブル列 */
  fields: Array<OnlineMeetingTableField> = [];
  /** テーブル行 */
  items: Array<OnlineMeetingTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.otherOfficeOnlineMeetings.futureTable.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.otherOfficeOnlineMeetings.futureTable.pageOptions;
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "", id: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];

  /** ### vue lifecycle mounted */
  public async mounted(): Promise<void> {
    const fields: Array<OnlineMeetingTableField> = this.createFields();
    this.fields = fields;

    await this.createView();
    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<OnlineMeetingTableField> {
    const fields: Array<OnlineMeetingTableField> = [];
    fields.push({ key: "startAndEnd", label: "日時", sortable: false, sortDirection: "desc", class: "start-and-end" });
    fields.push({ key: "isMain", label: "主催者", sortable: false, sortDirection: "desc", class: "is-main text-center" });
    fields.push({ key: "targetFacilityName", label: "会議相手", sortable: false, sortDirection: "desc", class: "target-facility-name" });
    fields.push({ key: "title", label: "件名", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "historyOperation", label: "操作", sortable: false, sortDirection: "desc", class: "history-operation text-center" });
    return fields;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    const otherOfficeConnections: Array<OtherOfficeConnectionGetModel> = await this.getOtherOfficeConnections();
    const filterOptions = this.createFilterOptions(otherOfficeConnections);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    if (this.historyPeriod == HistoryPeriodOptionValue.ALL) {
      this.setRows(undefined);

      const meetings: Array<OtherOfficeOnlineMeetingGetModel> = await this.getMeetings(undefined);
      this.items = meetings;
    } else {
      const today: Date = new Date();
      const end: number = this.$dateUtil.convertDateToNumber(today, "yyyymmdd235959000");

      switch (this.historyPeriod) {
        case HistoryPeriodOptionValue.THREE_MONTH: {
          today.setMonth(today.getMonth() - 3);
          break;
        }
        case HistoryPeriodOptionValue.SIX_MONTH: {
          today.setMonth(today.getMonth() - 6);
          break;
        }
        case HistoryPeriodOptionValue.ONE_YEAR: {
          today.setMonth(today.getFullYear() - 1);
          break;
        }
        case HistoryPeriodOptionValue.THREE_YEAR: {
          today.setFullYear(today.getFullYear() - 3);
          break;
        }
        default: {
          today.setDate(today.getDate() - 7);
          break;
        }
      }
      const start: number = this.$dateUtil.convertDateToNumber(today, "yyyymmdd000000000");
      this.setRows({ start, end });

      const meetings: Array<OtherOfficeOnlineMeetingGetModel> = await this.getMeetings({ start, end });
      this.items = meetings;
    }

    this.isLoading = false;

    return;
  }

  /** ### 他事業所連携管理を取得する */
  public async getOtherOfficeConnections(): Promise<Array<OtherOfficeConnectionGetModel>> {
    const response: Array<OtherOfficeConnectionGetModel> = await this.otherOfficeConnectionsService.gets(userStore);
    return response;
  }

  /**
   * ### 会議相手フィルター用選択項目
   */
  public createFilterOptions(connections: Array<OtherOfficeConnectionGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });
    for (const connection of connections) {
      if (connection.registerd === true && connection.registerdFacilityId != null) {
        filterOptions.push({ value: `${connection.registerdFacilityId}`, text: connection.officeName });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /** ### ページング用の全件数を設定する */
  public async setRows(params: { start: number; end: number } | undefined) {
    try {
      const count: number = await this.meetingsService.getCount(userStore, params);
      this.rows = count;
      return;
    } catch (error) {
      errorHandle(error, this);
    }
  }

  /** ### オンライン会議を取得する */
  public async getMeetings(params: { start: number; end: number } | undefined): Promise<Array<OtherOfficeOnlineMeetingGetModel>> {
    const response: Array<OtherOfficeOnlineMeetingGetModel> = await this.meetingsService.gets(userStore, params);
    return response;
  }

  /** ### 1ページあたりの表示行数変更イベント */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /** ### ページネーションのページ番号押下 */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 期間変更のセレクトボックス値変更イベント
   *
   * - 履歴を取得しなおす
   */
  public async changeHistoryPeriod(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  public onDetail(item: OnlineMeetingTableItem): void {
    tabIndexesStore.setOtherOfficeOnlineMeetingsTabIndex({ index: this.tabIndex });
    this.$router.push({ name: PcUrl.OtherOfficeOnlineMeetingDetail, params: { targetFacilityId: `${item.targetFacilityId}`, id: `${item.id}` } });
    return;
  }

  /** ### ダウンロードボタン押下イベント */
  public onDownload(): void {
    const csv: string = this.createCsv(this.items);
    this.downloadCsv(csv, `HitomeQコネクト-他事業所連携-オンライン会議.csv`);
    return;
  }

  /**
   * ### オンライン会議のCSVを作成する
   */
  public createCsv(items: Array<OnlineMeetingTableItem>): string {
    const csv: string[] = [];
    csv.push("\ufeff" + `日時,主催者,会議相手,件名`);

    for (const item of items) {
      const startAndEnd: string = this.convertStartAndEnd(item);
      const isMain: string = item.isMain == true ? "○" : "";
      const targetFacilityName: string = item.targetFacilityName;
      const title: string = item.title;
      csv.push(`${startAndEnd},${isMain},${targetFacilityName},${title}`);
    }

    return csv.join("\n");
  }

  public convertStartAndEnd(item: OnlineMeetingTableItem): string {
    const startDatetime = this.$dateUtil.convertNumberToString(item.startDatetime, "yyyy年mm月dd日 hh：MM");
    const endTime = this.$dateUtil.convertNumberToString(item.endDatetime, "hh：MM");
    return `${startDatetime}～${endTime}`;
  }

  /**
   * ### CSVファイルをダウンロードさせる
   *
   * - CSVの内容からblobファイルを作成する
   * - blobファイルの種別はtext/csv
   * - a要素を作ってそれを疑似的にクリックさせることでダウンロードさせる
   */
  public downloadCsv(csv: string, fileName: string): void {
    const blob: Blob = new Blob([csv], { type: "text/csv" });

    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();

    return;
  }

  setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
    }
    return;
  }
}

export enum HistoryPeriodOptionValue {
  UNKNOWN = 0,
  THREE_MONTH = 1,
  SIX_MONTH = 2,
  ONE_YEAR = 3,
  THREE_YEAR = 4,
  ALL = 5
}
</script>

<style lang="scss" scoped>
.history-online-meeting-list-tab {
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  .history-period {
    .title {
      font-weight: bold;
    }
    .history-option {
      width: 120px;
    }
  }
}
</style>
